import { useCallback, useEffect, useMemo, useState } from 'react';
import { RuleModel } from '../../../../types';
import SimpleFormPage, {
  getSchemaKeys,
} from '../../../shared/simple-form-page';
import { pagesConfig } from '../../../shared/pages-config';
import { FormSubSteps, StepConfig } from '../../../shared/shared-form-stepper';
import SimpleBreadcrumbsPage from '../../../shared/simple-breadcrumbs-page';
import {
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import {
  getAllPoliciesQuery,
  getDomainsQuery,
  getSharedDrivesByIdsQuery,
  getDocById,
  UserKeys,
} from '../../../api/users-api';
import * as analytics from '../../../shared/analytics/analytics.json';
import { useStore } from '../../state/zustand-store';
import { useAnalytics } from '../../../shared/analytics/create-events';
import { SubmitHandler, useForm } from 'react-hook-form';
import {
  addRule,
  RulesKeys,
  editRule,
  saveRule,
  listAllRules,
} from '../../../api/rules-engine-api';
import {
  cloneRule,
  getRulesEngineFormDefaultValues,
  prepareRuleData,
} from './rules-engine-form-utils';
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import { handleErrors } from '../../../utils/errors';
import { ModuleList } from '../../../shared/analytics/module-list';
import { useUserSettings } from '../../../utils/use-user-settings';
import {
  isAbleToSeeUIElement,
  RulesEngineRoles,
} from '../../../utils/roles/client-roles';
import DriveFiltersStep from './steps-old/drive-filters-step';
import ActionsStep from './steps-old/actions-step';
import ChangeLogDialog from '../../../shared/change-log-dialog';
import UnsavedChangesDialog, {
  useUnsavedChanges,
} from '../../../shared/unsaved-changes-dialog';
import RulesEngineBasicInformationStep from './steps-old/rules-engine-basic-information-step';
import RulesEngineFormSkeleton from './steps-old/rules-engine-form-skeleton';
import RulesEngineReviewStep from './steps-old/rules-engine-review-step';
import SharedFormStepper from '../../../shared/shared-form-stepper';
import { z, ZodObject, ZodRawShape } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { actionOptions } from './action-options';
import CalendarResourcesStep from './steps-old/calendar-resources-step';
import RulesEngineDrawerDetailsOld from './rules-engine-drawer-details-old';
import RulesEngineAdminFiltersStep from './steps-old/rules-engine-admin-filters-step';
import RulesEngineGroupFiltersStep from './steps-old/rules-engine-group-filters-step';
import DeleteRuleDialog from './delete-rule-dialog';
import RulesEnginePersonalCalendarFiltersStep from './steps-old/rules-engine-personal-calendar-filters-step';
import DisableRuleDialog from './disable-rule-dialog';
import EnableRuleDialog from './enable-rule-dialog';
import EditRuleDialog from './edit-rule-dialog';

import RuleSummaryPanel from './rule-summary-panel-old';
import NotFoundPage from '../../../shared/not-found-page';
import { Messages } from '../../../utils/message-constants';

import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Drawer from '@mui/material/Drawer';
import Stack from '@mui/material/Stack';

const EMPTY_ARRAY: [] = [];

export interface TriggerSource {
  type: string;
  value: string;
  driveFolderOwner?: string;
  driveFolderName?: string;
  driveFolderId?: string;
  selectedSharedDriveId?: string;
}

interface RulesEngineFormPageProps {
  pageMode: 'create' | 'edit' | 'clone';
  title: string;
}

export interface ProfileFieldFilter {
  profileInfoType?: string;
  profileFieldType?: string;
  profileFieldValues?: { value?: string }[];
  profileFieldValue?: string;
  profileFieldRegex?: string;
  profileFieldValueExact?: boolean;
  valueInputMode?: 'fieldValue' | 'regex';
  fieldDataType?: string;
}
export interface RulesEngineForm extends Omit<RuleModel, 'profileFieldFilter'> {
  triggerSources?: TriggerSource[];
  hasFileTypeFilter?: boolean;
  hasFileSizeFilter?: boolean;
  hasSharingPermissionsFilter?: boolean;
  hasSelectedDomainSharing?: boolean;
  selectedSharingOption?:
    | 'fileIsSharedToDomain'
    | 'fileIsSharedPublic'
    | 'fileSharedToSpecificUser'
    | 'fileSharedToUserExternalToDomain'
    | 'fileSharedToUserInternalToDomain';
  sizeComparison?: 'larger' | 'smaller' | 'between';
  sizeType?: 'MB' | 'KB' | 'Byte';
  hasProfileFieldFilter?: boolean;
  groupInfo?: {
    groupEmail: string[];
    groupRole: string[];
  };
  removeGroupInfo?: {
    groupEmail: string[];
  };
  profileFieldFilter?: ProfileFieldFilter;
  allSource?: boolean;
  targetsExcluded?: string[];
}

const TriggerSourceSchema = z.object({
  type: z.string(),
  value: z.string(),
  driveFolderOwner: z.string().optional(),
  driveFolderId: z.string().optional(),
  driveFolderName: z.string().optional(),
  selectedSharedDriveId: z.string().optional(),
});

const DriveFolderSourceSchema = z.object({
  driveFolderId: z.string(),
  driveFolderOwner: z.string(),
});

const BasicInformationSchema = z
  .object({
    basicInfo: z.object({
      ruleName: z.string().min(1, 'Name is required'),
      description: z.string(),
    }),
    triggerInfo: z.object({
      triggerGroup: z.string(),
      triggerEvent: z.string(),
      occursEverytime: z.boolean(),
      triggerThreshold: z
        .number()
        .refine((val) => val === undefined || val > 0, {
          message: 'Threshold must be greater than 0',
        }),
      hasTimeDuration: z.boolean(),
      triggerDuration: z
        .number()
        .refine((val) => val === undefined || val > 0, {
          message: 'Duration must be greater than 0',
        }),
      triggerDurationType: z.string(),
      triggerDomainSources: z.array(z.string()),
      triggerGroupSources: z.array(z.string()),
      triggerOrgSources: z.array(z.string()),
      triggerUserSources: z.array(z.string()),
      driveRegExs: z.array(z.string()),
      sharedDriveIds: z.array(z.string()),

      driveFolderTriggerSources: z.array(DriveFolderSourceSchema),
    }),
    triggerSources: z.array(TriggerSourceSchema).optional(),
    allSource: z.boolean().optional(),
    targetsExcluded: z.array(z.string()).optional(),
  })
  .superRefine((data, ctx) => {
    if (
      data.triggerInfo.triggerGroup !== 'calendar' &&
      !data.allSource &&
      data.triggerSources &&
      data.triggerSources.length <= 0
    ) {
      ctx.addIssue({
        code: 'custom',
        message: 'At least one trigger source is required.',
        path: ['triggerSources'],
      });
    }
  });

const CalendarResourcesStepSchema = z.object({
  allSource: z.boolean(),
  triggerInfo: z.object({
    triggerCalendarSources: z.array(z.string()),
  }),
});

const DriveFiltersSchema = z
  .object({
    hasFileTypeFilter: z.boolean().optional(),
    hasFileSizeFilter: z.boolean().optional(),
    sizeComparison: z.enum(['larger', 'smaller', 'between']).optional(),
    sizeType: z.enum(['MB', 'KB', 'Byte']).optional(),
    hasSharingPermissionsFilter: z.boolean().optional(),
    selectedSharingOption: z
      .enum([
        'fileIsSharedToDomain',
        'fileIsSharedPublic',
        'fileSharedToSpecificUser',
        'fileSharedToUserExternalToDomain',
        'fileSharedToUserInternalToDomain',
      ])
      .optional(),
    hasSelectedDomainSharing: z.boolean().optional(),
    triggerInfo: z.object({
      fileMimeTypes: z.array(z.string()),
      driveFileSizeFloor: z.number(),
      driveFileSizeCeiling: z.number(),
      fileIsSharedToDomain: z.boolean(),
      fileIsSharedPublic: z.boolean(),
      fileSharedToUserExternalToDomain: z.boolean().catch(false),
      fileSharedToUserInternalToDomain: z.boolean().catch(false),
      fileSharedToSpecificUser: z.array(z.string()),
      canFindInSearchResults: z.boolean().catch(false),
      mustHaveLinkToAccess: z.boolean().catch(false),
    }),
  })
  .refine(
    (data) => {
      if (data.hasFileTypeFilter) {
        return (
          data.triggerInfo.fileMimeTypes &&
          data.triggerInfo.fileMimeTypes.length > 0
        );
      }
      return true;
    },
    {
      message: 'Please select a file type.',
      path: ['triggerInfo', 'fileMimeTypes'],
    }
  )
  .refine(
    (data) => {
      if (data.hasFileSizeFilter) {
        const floor = data.triggerInfo.driveFileSizeFloor;
        const comparison = data.sizeComparison;
        switch (comparison) {
          case 'larger':
          case 'between':
            return floor > 0;
          default:
            return true;
        }
      }
      return true;
    },
    {
      message: 'Must be greater than 0.',
      path: ['triggerInfo', 'driveFileSizeFloor'],
    }
  )
  .refine(
    (data) => {
      if (data.hasFileSizeFilter) {
        const ceiling = data.triggerInfo.driveFileSizeCeiling;
        const comparison = data.sizeComparison;
        switch (comparison) {
          case 'smaller':
          case 'between':
            return ceiling > 0;
          default:
            return true;
        }
      }
      return true;
    },
    {
      message: 'Must be greater than 0.',
      path: ['triggerInfo', 'driveFileSizeCeiling'],
    }
  )
  .refine(
    (data) => {
      if (data.hasFileSizeFilter) {
        const ceiling = data.triggerInfo.driveFileSizeCeiling ?? 0;
        const floor = data.triggerInfo.driveFileSizeFloor ?? 0;
        const comparison = data.sizeComparison;
        if (comparison === 'between') {
          return floor > 0 && ceiling > floor;
        }
      }
      return true;
    },
    {
      message: 'Must be larger than floor.',
      path: ['triggerInfo', 'driveFileSizeCeiling'],
    }
  )
  .refine(
    (data) => {
      if (
        data.hasSharingPermissionsFilter &&
        data.selectedSharingOption === 'fileSharedToSpecificUser'
      ) {
        return (
          data.triggerInfo.fileSharedToSpecificUser &&
          data.triggerInfo.fileSharedToSpecificUser.length > 0
        );
      }
      return true;
    },
    {
      message: 'Please select a user.',
      path: ['triggerInfo', 'fileSharedToSpecificUser'],
    }
  );

const ActionsSchema = z
  .object({
    actionInfo: z.object({
      actionTodo: z
        .array(
          z.enum(
            actionOptions.map((action) => action.value) as [string, ...string[]]
          )
        )
        .min(1, 'Please add at least one action.'),
      payload: z.string(),
      endpointUrl: z.string(),
      requestMethod: z.string(),
      newPassword: z.string(),
      timeZone: z.string(),
      occurrenceLimit: z.number(),
    }),
    emailInfo: z.object({
      emailNotificationEmails: z.array(z.string()),
      ccEmails: z.array(z.string()),
      bccEmails: z.array(z.string()),
      emailSubject: z.string(),
      emailMessage: z.string(),
    }),
    chatInfo: z.object({
      chatUrl: z.string(),
      chatMessage: z.string(),
    }),
    policyInfo: z.object({
      policyId: z.number(),
      policyType: z.string(),
    }),
    groupInfo: z.object({
      groupEmail: z.array(z.string()),
      groupRole: z.array(z.string()),
    }),
    removeGroupInfo: z.object({
      groupEmail: z.array(z.string()),
    }),
    groupSettingsInfo: z.object({
      groupTemplateId: z.union([z.number(), z.string()]),
    }),
  })
  .refine(
    (data) => {
      if (data.actionInfo.actionTodo.includes('limitOccurrences')) {
        return (
          data.actionInfo.occurrenceLimit && data.actionInfo.occurrenceLimit > 0
        );
      }
      return true;
    },
    {
      message: 'Occurrence limit must be greater than 0 for calendar events.',
      path: ['actionInfo', 'occurrenceLimit'],
    }
  )
  .refine(
    (data) => {
      if (
        data.actionInfo.actionTodo.includes('sendEmail') ||
        data.actionInfo.actionTodo.includes('sendEmailTemplate')
      ) {
        return (
          data.emailInfo.emailNotificationEmails &&
          data.emailInfo.emailNotificationEmails.length > 0
        );
      }
      return true;
    },
    {
      message: 'Please select at least one email recipient.',
      path: ['emailInfo', 'emailNotificationEmails'],
    }
  )
  .refine(
    (data) => {
      if (data.actionInfo.actionTodo.includes('sendChatMessage')) {
        return data.chatInfo.chatUrl;
      }
      return true;
    },
    { message: 'Chat URL are required.', path: ['chatInfo', 'chatUrl'] }
  )
  .refine(
    (data) => {
      if (data.actionInfo.actionTodo.includes('sendChatMessage')) {
        return data.chatInfo.chatMessage;
      }
      return true;
    },
    { message: 'Chat message are required.', path: ['chatInfo', 'chatMessage'] }
  )
  .refine(
    (data) => {
      if (data.actionInfo.actionTodo.includes('runPolicy')) {
        return data.policyInfo.policyId;
      }
      return true;
    },
    { message: 'Please select a policy.', path: ['policyInfo', 'policyId'] }
  )
  .refine(
    (data) => {
      if (data.actionInfo.actionTodo.includes('addToGroup')) {
        return data.groupInfo.groupEmail.length > 0;
      }
      return true;
    },
    {
      message: 'Please select at least one group.',
      path: ['groupInfo', 'groupEmail'],
    }
  )
  .refine(
    (data) => {
      if (data.actionInfo.actionTodo.includes('removeFromGroup')) {
        return data.removeGroupInfo.groupEmail.length > 0;
      }
      return true;
    },
    {
      message: 'Please select at least one group.',
      path: ['removeGroupInfo', 'groupEmail'],
    }
  )
  .refine(
    (data) => {
      if (data.actionInfo.actionTodo.includes('applyGroupTemplate')) {
        return Number(data.groupSettingsInfo.groupTemplateId) > 0;
      }
      return true;
    },
    {
      message: 'Please select a group template.',
      path: ['groupSettingsInfo', 'groupTemplateId'],
    }
  );

function validateFieldValue(val: string, fieldDataType: string): string | null {
  switch (fieldDataType) {
    case 'WHOLE_NUMBER':
      return /^-?\d+$/.test(val) ? null : 'Must be a whole number.';
    case 'DECIMAL':
      return /^-?\d+(\.\d+)?$/.test(val) ? null : 'Must be a decimal number.';
    case 'DATE':
      return /^\d{4}-\d{2}-\d{2}$/.test(val)
        ? null
        : 'Must be a valid date (YYYY-MM-DD).';
    case 'EMAIL':
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val)
        ? null
        : 'Must be a valid email address.';
    case 'PHONE':
      return /^\+?[0-9\s\-().]{7,20}$/.test(val)
        ? null
        : 'Must be a valid phone number.';
    case 'BOOL':
      return val === 'true' || val === 'false' ? null : 'Must be Yes or No.';
    default:
      return null;
  }
}

const AdminFiltersSchema = z
  .object({
    hasProfileFieldFilter: z.boolean().optional(),
    profileFieldFilter: z.object({
      profileInfoType: z.string().optional(),
      profileFieldType: z.string().optional(),
      profileFieldValues: z
        .array(z.object({ value: z.string().optional() }))
        .optional(),
      profileFieldValue: z.string().optional(),
      profileFieldValueExact: z.boolean().optional(),
      profileFieldRegex: z.string().optional(),
      valueInputMode: z.enum(['fieldValue', 'regex']).optional(),
      fieldDataType: z.string().optional(),
    }),
  })
  .superRefine((data, ctx) => {
    if (!data.hasProfileFieldFilter) return;

    const {
      profileFieldType,
      profileFieldValues,
      profileFieldRegex,
      valueInputMode,
      fieldDataType,
    } = data.profileFieldFilter;
    const mode = valueInputMode ?? 'fieldValue';

    if (!profileFieldType) {
      ctx.addIssue({
        code: 'custom',
        message: 'Profile field type is required.',
        path: ['profileFieldFilter', 'profileFieldType'],
      });
    }

    if (data.hasProfileFieldFilter && mode === 'fieldValue') {
      const hasValues =
        (profileFieldValues ?? []).some(
          (v) => v.value && v.value.trim() !== ''
        ) || (data.profileFieldFilter.profileFieldValue ?? '').trim() !== '';

      if (!hasValues) {
        ctx.addIssue({
          code: 'custom',
          message: 'At least one field value is required.',
          path: ['profileFieldFilter', 'profileFieldValues', 0, 'value'],
        });
      }

      profileFieldValues?.forEach((item, i) => {
        if (!item.value || item.value.trim() === '') return;
        const issue = validateFieldValue(item.value, fieldDataType ?? 'TEXT');
        if (issue) {
          ctx.addIssue({
            code: 'custom',
            message: issue,
            path: ['profileFieldFilter', 'profileFieldValues', i, 'value'],
          });
        }
      });
    }

    if (data.hasProfileFieldFilter && mode === 'regex') {
      if (!profileFieldRegex?.trim()) {
        ctx.addIssue({
          code: 'custom',
          message: 'A regular expression is required.',
          path: ['profileFieldFilter', 'profileFieldRegex'],
        });
      } else {
        try {
          new RegExp(profileFieldRegex);
        } catch {
          ctx.addIssue({
            code: 'custom',
            message: 'Invalid regular expression.',
            path: ['profileFieldFilter', 'profileFieldRegex'],
          });
        }
      }
    }
  });

const GroupFiltersSchema = z
  .object({
    groupFilter: z
      .object({
        hasGroupNameFilter: z.boolean().optional(),
        groupNameMode: z.enum(['name', 'regex']).optional(),
        groupName: z.string().optional(),
        groupNameExact: z.boolean().optional(),
        groupNameRegex: z.string().optional(),
        hasMemberCountFilter: z.boolean().optional(),
        memberCountMode: z.enum(['single', 'range']).optional(),
        memberCountOperator: z
          .enum([
            'equals',
            'greaterThan',
            'lessThan',
            'greaterThanOrEqual',
            'lessThanOrEqual',
          ])
          .optional(),
        memberCount: z.number().optional(),
        memberCountRangeLessThan: z.number().optional(),
        memberCountRangeGreaterThan: z.number().optional(),
      })
      .optional(),
  })
  .superRefine((data, ctx) => {
    const filter = data.groupFilter;
    if (!filter) return;

    if (filter.hasGroupNameFilter) {
      const mode = filter.groupNameMode ?? 'name';
      if (mode === 'name' && !(filter.groupName ?? '').trim()) {
        ctx.addIssue({
          code: 'custom',
          message: 'A group name is required.',
          path: ['groupFilter', 'groupName'],
        });
      }
      if (mode === 'regex') {
        if (!(filter.groupNameRegex ?? '').trim()) {
          ctx.addIssue({
            code: 'custom',
            message: 'A regular expression is required.',
            path: ['groupFilter', 'groupNameRegex'],
          });
        } else {
          try {
            new RegExp(filter.groupNameRegex as string);
          } catch {
            ctx.addIssue({
              code: 'custom',
              message: 'Invalid regular expression.',
              path: ['groupFilter', 'groupNameRegex'],
            });
          }
        }
      }
    }

    if (filter.hasMemberCountFilter) {
      const mode = filter.memberCountMode ?? 'single';
      const isInvalidCount = (val: number | undefined | null) =>
        val === undefined || val === null || Number.isNaN(val) || val < 0;

      if (mode === 'single') {
        if (isInvalidCount(filter.memberCount)) {
          ctx.addIssue({
            code: 'custom',
            message: 'A valid member count is required.',
            path: ['groupFilter', 'memberCount'],
          });
        }
      } else {
        if (isInvalidCount(filter.memberCountRangeLessThan)) {
          ctx.addIssue({
            code: 'custom',
            message: 'A valid value is required.',
            path: ['groupFilter', 'memberCountRangeLessThan'],
          });
        }
        if (isInvalidCount(filter.memberCountRangeGreaterThan)) {
          ctx.addIssue({
            code: 'custom',
            message: 'A valid value is required.',
            path: ['groupFilter', 'memberCountRangeGreaterThan'],
          });
        }
        if (
          !isInvalidCount(filter.memberCountRangeLessThan) &&
          !isInvalidCount(filter.memberCountRangeGreaterThan) &&
          (filter.memberCountRangeGreaterThan as number) <
            (filter.memberCountRangeLessThan as number)
        ) {
          ctx.addIssue({
            code: 'custom',
            message:
              "The 'greater than' value must be greater than or equal to the 'less than' value.",
            path: ['groupFilter', 'memberCountRangeGreaterThan'],
          });
        }
      }
    }
  });

const PersonalCalendarFiltersSchema = z
  .object({
    personalCalendarFilter: z
      .object({
        hasPersonalCalendarFilter: z.boolean().optional(),
        sharedToUserExternalToDomain: z.boolean().optional(),
        sharedToUserInternalToDomain: z.boolean().optional(),
        sharedToSpecificUsers: z.array(z.string()).optional(),
        sharedToGroups: z.array(z.string()).optional(),
      })
      .optional(),
  })
  .superRefine((data, ctx) => {
    const filter = data.personalCalendarFilter;
    if (!filter || !filter.hasPersonalCalendarFilter) return;

    const isAnyFilterSelected =
      filter.sharedToUserExternalToDomain ||
      filter.sharedToUserInternalToDomain ||
      (filter.sharedToSpecificUsers &&
        filter.sharedToSpecificUsers.length > 0) ||
      (filter.sharedToGroups && filter.sharedToGroups.length > 0);

    if (!isAnyFilterSelected) {
      ctx.addIssue({
        code: 'custom',
        message: 'At least one calendar sharing filter must be selected.',
        path: ['personalCalendarFilter', 'hasPersonalCalendarFilter'],
      });
    }
  });

export const RulesEngineSchema = BasicInformationSchema.and(DriveFiltersSchema)
  .and(CalendarResourcesStepSchema)
  .and(ActionsSchema)
  .and(
    z.object({
      id: z.number().catch(0),
      basicInfo: z.object({
        status: z.enum(['Enabled', 'Disabled', 'Draft']).catch('Draft'),
        creator: z.string().catch(''),
        createdDate: z.number().catch(0),
        lastModifiedDate: z.number().catch(0),
      }),
    })
  )
  .and(AdminFiltersSchema)
  .and(GroupFiltersSchema)
  .and(PersonalCalendarFiltersSchema)
  .superRefine((data, ctx) => {
    if (
      data.triggerInfo.triggerGroup === 'calendar' &&
      data.triggerInfo.triggerEvent !== 'change_calendar_acls' &&
      !data.allSource &&
      data.triggerInfo.triggerCalendarSources &&
      data.triggerInfo.triggerCalendarSources.length <= 0
    ) {
      ctx.addIssue({
        code: 'custom',
        message: 'At least one trigger source is required for calendar events.',
        path: ['triggerInfo', 'triggerCalendarSources'],
      });
    }
  });

export type RulesEngineFormValues = z.infer<typeof RulesEngineSchema>;
export default function RulesEngineFormPageOld({
  pageMode,
  title,
}: RulesEngineFormPageProps) {
  const { changeLogRoles, loggedInUser, toolsRoles, features } =
    useUserSettings();
  const { setSnackbar } = useStore();
  const { createEvent } = useAnalytics();
  const queryClient = useQueryClient();
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [dialogSubmitAction, setDialogSubmitAction] = useState<
    'publish' | 'draft' | null
  >(null);
  const [changeLogMessage, setChangeLogMessage] = useState('');
  const [activeStep, setActiveStep] = useState(0);
  const [actionSubSteps, setActionSubSteps] = useState<FormSubSteps[]>([]);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(() => new Set());

  const [sourceType, setSourceType] = useState<
    'User' | 'Group' | 'Orgs' | 'Domain'
  >('Orgs');
  const [searchParams, setSearchParams] = useSearchParams();
  const { id: selectedRuleIdFromPath } = useParams();
  const location = useLocation();
  const fromTemplates = location.state?.from === 'templates';
  const templateFromState = location.state?.template as RuleModel | undefined;
  const selectedRuleIdFromUrl = searchParams.get('selectedRule')
    ? searchParams.get('selectedRule')
    : pageMode === 'edit' && selectedRuleIdFromPath;

  const {
    data: allPolicies,
    isLoading: isPolicyLoading,
    isFetching: isPolicyFetching,
  } = useQuery(getAllPoliciesQuery());

  const [openDeleteRuleDialog, setOpenDeleteRuleDialog] = useState(false);
  const [openDisableRuleDialog, setOpenDisableRuleDialog] = useState(false);
  const [openEnableRuleDialog, setOpenEnableRuleDialog] = useState(false);
  const [openEditRuleDialog, setOpenEditRuleDialog] = useState(false);

  const baseSteps: StepConfig[] = [
    {
      label: 'Basic Information',
      schema: BasicInformationSchema,
    },
    {
      label: 'Trigger Sources',
      schema: CalendarResourcesStepSchema,
    },
    {
      label: 'Drive Filters',
      schema: DriveFiltersSchema,
    },
    {
      label: 'Admin Filters',
      schema: AdminFiltersSchema,
    },
    {
      label: 'Group Filters',
      schema: GroupFiltersSchema,
    },
    {
      label: 'Personal Calendar Filters',
      schema: PersonalCalendarFiltersSchema,
    },
    {
      label: 'Actions',
      schema: ActionsSchema,
    },
    {
      label: 'Review',
    },
  ];

  const { data: allRulesFromApi, isLoading: isLoadingAllRules } = useQuery({
    queryKey: [RulesKeys.RulesList],
    queryFn: listAllRules,
  });

  const sourceRuleForForm = useMemo(() => {
    if (pageMode === 'create' || !allRulesFromApi) return null;

    const rule = allRulesFromApi.find(
      (r) => r.id.toString() === selectedRuleIdFromPath
    );

    if (!rule) return null;

    return pageMode === 'clone' ? cloneRule(rule) : rule;
  }, [allRulesFromApi, pageMode, selectedRuleIdFromPath]);

  const selectedRule =
    allRulesFromApi?.find((r) => r.id.toString() === selectedRuleIdFromUrl) ||
    null;

  const ruleId = sourceRuleForForm?.id || 0;
  const lastSavedDate = sourceRuleForForm?.basicInfo.lastModifiedDate || null;

  const sharedDriveIdsToFetch =
    sourceRuleForForm?.triggerInfo?.sharedDriveIds || EMPTY_ARRAY;
  const hasBeenSaved = pageMode === 'edit' && !!sourceRuleForForm;
  const { data: sharedDrives } = useQuery(
    getSharedDrivesByIdsQuery(sharedDriveIdsToFetch)
  );

  const userDriveSourcesToFetch =
    sourceRuleForForm?.triggerInfo?.driveFolderTriggerSources || EMPTY_ARRAY;

  const userDriveFoldersQueries = useMemo(
    () =>
      userDriveSourcesToFetch
        .filter((s) => s.driveFolderId && s.driveFolderOwner)
        .map((source) => ({
          queryKey: [
            UserKeys.DriveFolder,
            source.driveFolderId,
            source.driveFolderOwner,
          ],
          queryFn: () =>
            getDocById(source.driveFolderOwner, source.driveFolderId),
        })),
    [userDriveSourcesToFetch]
  );

  // The combine function now just passes through the raw results array from useQueries
  const combineUserDriveFolders = useCallback(
    (results: any[]) => ({
      rawResults: results,
      pending: results.some((result) => result.isPending),
      isError: results.some((result) => result.isError),
    }),
    []
  );

  const {
    rawResults: rawUserDriveFolderResults,
    pending: pendingUserDriveFolders,
    isError: hasUserDriveFolderError,
  } = useQueries({
    queries: userDriveFoldersQueries,
    combine: combineUserDriveFolders,
  });

  // Process the raw results into a stable array of Doc objects using useMemo
  const userDriveFolders = useMemo(() => {
    return rawUserDriveFolderResults
      .filter((result) => result.status === 'success' && result.data)
      .map((result) => result.data);
  }, [rawUserDriveFolderResults]);

  useEffect(() => {
    if (hasUserDriveFolderError) {
      setSnackbar({
        open: true,
        severity: 'error',
        message:
          'Failed to load some folder details. Please refresh the page. Saving now may result in data loss.',
      });
    }
  }, [hasUserDriveFolderError, setSnackbar]);

  const openRulesDetailsDrawer =
    !!searchParams.get('selectedRule') && !!selectedRule && !isLoadingAllRules;

  const navigate = useNavigate();
  const {
    autoSelectPolicyId,
    autoSelectEmailTemplateId,
    emailSubject: emailSubjectFromState,
    emailMessage: emailMessageFromState,
  } = (location.state as {
    autoSelectPolicyId?: number;
    emailSubject?: string;
    emailMessage?: string;
    autoSelectEmailTemplateId?: number;
  } | null) || {};

  const formValues = useMemo(() => {
    if (sourceRuleForForm) {
      return getRulesEngineFormDefaultValues({
        pageMode: pageMode,
        selectedRule: sourceRuleForForm, // For edit/clone
        allPolicies,
        sharedDrives,
        userDriveFolders,
        loggedInUser,
        features,
      });
    } else if (pageMode === 'create' && templateFromState) {
      return getRulesEngineFormDefaultValues({
        pageMode: 'clone', // Treat template as a clone to hydrate form
        selectedRule: templateFromState,
        allPolicies,
        sharedDrives,
        userDriveFolders,
        loggedInUser,
        autoSelectPolicyId,
        emailSubject: emailSubjectFromState,
        emailMessage: emailMessageFromState,
        features,
      });
    }
    return getRulesEngineFormDefaultValues({
      pageMode: pageMode,
      allPolicies,
      userDriveFolders,
      sharedDrives,
      loggedInUser,
      autoSelectPolicyId,
      emailSubject: emailSubjectFromState,
      emailMessage: emailMessageFromState,
      features,
    });
  }, [
    sourceRuleForForm,
    pageMode,
    templateFromState,
    loggedInUser,
    allPolicies,
    sharedDrives,
    userDriveFolders,
    autoSelectPolicyId,
    emailSubjectFromState,
    emailMessageFromState,
    features,
  ]);

  const { data: domains } = useQuery(getDomainsQuery());

  const {
    control,
    watch,
    setValue,
    getValues,
    handleSubmit,
    formState: { errors, isValidating, isSubmitting, isDirty },
    reset,
    trigger,
    clearErrors,
  } = useForm<RulesEngineFormValues>({
    values: formValues,
    resolver: zodResolver(RulesEngineSchema),
    reValidateMode: 'onChange',
    defaultValues: {
      basicInfo: {
        // Keep minimal defaults, `values` will populate the rest
        ruleName: '',
        description: '',
      },
      triggerInfo: {
        occursEverytime: true,
        hasTimeDuration: false,
        triggerGroup: 'admin',
        triggerEvent: 'CREATE_USER',
        triggerThreshold: 1,
        triggerDuration: 1,
        triggerDurationType: 'days',
        triggerDomainSources: [],
        triggerGroupSources: [],
        triggerOrgSources: [],
        triggerUserSources: [],
        triggerCalendarSources: [],
        sharedDriveIds: [],
        driveRegExs: [],
        driveFolderTriggerSources: [],
        driveFileSizeFloor: 0,
        driveFileSizeCeiling: 0,
        //driveFileThreshold: 0,
        fileMimeTypes: [],
        fileIsSharedToDomain: false,
        fileIsSharedPublic: false,
        fileSharedToSpecificUser: [],
        canFindInSearchResults: false,
        mustHaveLinkToAccess: false,
        fileSharedToUserExternalToDomain: false,
        fileSharedToUserInternalToDomain: false,
      },
      actionInfo: {
        actionTodo: [],
        payload: '',
        endpointUrl: '',
        requestMethod: 'POST',
        newPassword: '',
        timeZone: 'UTC',
        occurrenceLimit: 0,
      },
      emailInfo: {
        emailNotificationEmails: [],
        ccEmails: [],
        bccEmails: [],
        emailSubject: '',
        emailMessage: '',
      },
      chatInfo: {
        chatUrl: '',
        chatMessage: '',
      },
      policyInfo: {
        policyId: 0,
        policyType: '',
      },
      allSource: false,
      targetsExcluded: [],
      groupInfo: {
        groupEmail: [],
        groupRole: [],
      },
      removeGroupInfo: {
        groupEmail: [],
      },
      groupSettingsInfo: {
        groupTemplateId: 0,
      },
      triggerSources: [],
      profileFieldFilter: {
        profileInfoType: '',
        profileFieldType: '',
        profileFieldValues: [{ value: '' }],
        profileFieldValue: '',
        profileFieldRegex: '',
        profileFieldValueExact: false,
        valueInputMode: 'fieldValue',
        fieldDataType: '',
      },
      hasProfileFieldFilter: false,
      hasFileTypeFilter: false,
      hasFileSizeFilter: false,
      hasSharingPermissionsFilter: false,
      hasSelectedDomainSharing: false,
      sizeComparison: 'larger',
      sizeType: 'MB',
      selectedSharingOption: undefined,
    },
  });

  const triggerGroup = watch('triggerInfo.triggerGroup');
  const triggerEvent = watch('triggerInfo.triggerEvent');

  const steps = useMemo(() => {
    const stepVisibilityRules: {
      [key: string]: (group: string, event: string) => boolean;
    } = {
      'Drive Filters': (group, event) =>
        group === 'drive' &&
        ['create', 'download', 'change_user_access', 'change_owner'].includes(
          event
        ),
      'Admin Filters': (group, event) =>
        group === 'admin' &&
        ['CREATE_USER', 'ROLE_PERMISSION_CHANGE', 'UPDATE'].includes(event),
      'Group Filters': (group, event) =>
        (group === 'admin' &&
          [
            'ADD_GROUP_MEMBER',
            'REMOVE_GROUP_MEMBER',
            'UPDATE_GROUP_MEMBER',
          ].includes(event)) ||
        group === 'groupEvents',
      'Personal Calendar Filters': (group, event) =>
        group === 'calendar' && ['change_calendar_acls'].includes(event),
      'Trigger Sources': (group, event) =>
        group === 'calendar' &&
        ['create_event', 'change_event'].includes(event),
    };

    return baseSteps.filter(
      (step) =>
        stepVisibilityRules[step.label]?.(triggerGroup, triggerEvent) ?? true
    );
  }, [triggerGroup, triggerEvent]);

  const [stepCompleted, setStepCompleted] = useState(() =>
    steps.map(() => false)
  );

  const { mutate: saveDraft, isPending: isSavingDraft } = useMutation({
    mutationFn: (data: RulesEngineForm & { showSnackbar?: boolean }) => {
      const { showSnackbar, ...dataWithoutFlag } = data;
      const formattedData = prepareRuleData(dataWithoutFlag);
      formattedData.id = ruleId;
      formattedData.basicInfo.status = 'Draft';
      formattedData.basicInfo.lastModifiedDate = Date.now();

      // Set createdDate for new drafts (when ruleId is 0)
      if (ruleId === 0) {
        formattedData.basicInfo.createdDate = Date.now();
      }

      return saveRule(formattedData, changeLogMessage).then((result) => ({
        ...result,
        showSnackbar,
      }));
    },
    onSuccess: (savedRule) => {
      const { showSnackbar, ...ruleData } = savedRule;

      // Update the query cache to reflect the newly saved rule.
      // This makes the cache the source of truth and avoids stale local state.
      queryClient.setQueryData(
        [RulesKeys.RulesList],
        (oldData: RuleModel[] | undefined) => {
          const ruleExists = oldData?.some((r) => r.id === ruleData.id);
          if (ruleExists) {
            return oldData?.map((r) => (r.id === ruleData.id ? ruleData : r));
          }
          return [...(oldData || []), ruleData];
        }
      );

      setValue(
        'basicInfo.lastModifiedDate',
        ruleData.basicInfo.lastModifiedDate
      );
      if (showSnackbar) {
        setSnackbar({
          open: true,
          severity: 'success',
          message: 'Rule saved as draft. You can resume editing anytime.',
        });

        bypass();
        navigate(`/${pagesConfig.rulesEngine.link}`);
      }
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage:
          'There was an issue saving the draft. Please try again.',
      });
    },
  });

  const { mutate, isPending } = useMutation({
    mutationFn: (data: RulesEngineForm) => {
      const formattedData = prepareRuleData(data);
      formattedData.basicInfo.status = 'Enabled';
      if (pageMode === 'edit') {
        return editRule(formattedData, changeLogMessage);
      } else {
        return addRule(formattedData, changeLogMessage);
      }
    },
    onSuccess: (returnedData) => {
      const successMessage =
        pageMode === 'edit'
          ? Messages.Success.Updated('Automation rule')
          : Messages.Success.Created('Automation rule');

      setSnackbar({
        open: true,
        severity: 'success',
        message: successMessage,
      });

      queryClient.invalidateQueries({ queryKey: [RulesKeys.RulesList] });
      setChangeLogMessage('');

      if (pageMode === 'edit') {
        const formCompatibleData = getRulesEngineFormDefaultValues({
          pageMode: 'edit',
          selectedRule: returnedData,
          allPolicies,
          sharedDrives,
          userDriveFolders,
          loggedInUser,
          features,
        });
        reset(formCompatibleData);
      } else {
        reset();
      }

      bypass();
      navigate(`/${pagesConfig.rulesEngine.link}`);
    },
    onError: (error) => {
      const errorMessage =
        pageMode === 'edit'
          ? Messages.Errors.Network.Updating('this automation rule')
          : Messages.Errors.Network.Adding('this automation rule');
      handleErrors({
        error,
        snackbarMessage: errorMessage,
      });
    },
    onSettled: (_returnData, _error, data) => {
      const eventType =
        pageMode === 'edit'
          ? analytics.automation.rules_engine.edit_rule
          : analytics.automation.rules_engine.create_rule;

      const eventPayload = {
        module: ModuleList.modules.automation.name,
        section: ModuleList.modules.automation.sections.rulesEngine,
        rule: {
          triggerGroup: data.triggerInfo?.triggerGroup,
          triggerEvent: data.triggerInfo?.triggerEvent,
          occursEverytime: data.triggerInfo?.occursEverytime,
          triggerThreshold: data.triggerInfo?.triggerThreshold,
          hasTimeDuration: data.triggerInfo?.hasTimeDuration,
          triggerDuration: data.triggerInfo?.triggerDuration,
          triggerDurationType: data.triggerInfo?.triggerDurationType,
          triggerUserSources: data.triggerInfo?.triggerUserSources?.length || 0,
          triggerCalendarSources:
            data.triggerInfo?.triggerCalendarSources?.length || 0,
          triggerGroupSources:
            data.triggerInfo?.triggerGroupSources?.length || 0,
          triggerOrgSources: data.triggerInfo?.triggerOrgSources?.length || 0,
          triggerDomainSources:
            data.triggerInfo?.triggerDomainSources?.length || 0,
          sharedDriveIds: data.triggerInfo?.sharedDriveIds?.length || 0,
          driveRegExs: data.triggerInfo?.driveRegExs?.length || 0,
          action: data.actionInfo?.actionTodo,
        },
      };

      createEvent(eventType, eventPayload);
      setOpenChangeLogDialog(false);
    },
  });

  const onSubmit: SubmitHandler<RulesEngineForm> = async (data) => {
    const requiredRole =
      pageMode === 'edit'
        ? RulesEngineRoles.EditRules.value
        : RulesEngineRoles.AddRules.value;

    if (isAbleToSeeUIElement(changeLogRoles, requiredRole)) {
      setDialogSubmitAction('publish');
      setOpenChangeLogDialog(true);
    } else {
      const isValid = await trigger();
      if (isValid) {
        mutate(data);
      }
    }
  };

  const handleCancel = () => {
    const destination = fromTemplates
      ? `/${pagesConfig.rulesEngine.link}/${pagesConfig.browseTemplates.link}`
      : `/${pagesConfig.rulesEngine.link}`;
    navigate(destination, {
      state: { fromCancelButton: true },
    });
  };

  const handleNext = async (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();

    // Validate current step using schema
    const currentStepSchema = steps[activeStep].schema;
    let isValid = true;

    if (currentStepSchema) {
      // Use getSchemaKeys to get all field paths from the schema
      const fields = getSchemaKeys(
        currentStepSchema as unknown as ZodObject<ZodRawShape>
      );
      isValid = await trigger(
        fields as unknown as Parameters<typeof trigger>[0]
      );
    }
    if (isValid) {
      // Move to next step
      setActiveStep((prevState) => prevState + 1);
      setStepCompleted((prevState) => {
        const newCompleted = [...prevState];
        newCompleted[activeStep] = true;
        return newCompleted;
      });
    } else {
      if (steps[activeStep].label === 'Actions') {
        const actions = getValues('actionInfo.actionTodo') || [];

        const firstErrorAction = [...actions].reverse().find((action) => {
          const actionTitle = actionOptions.find((opt) => opt.value === action)
            ?.displayValue;
          return actionSubSteps.some(
            (subStep) => subStep.title === actionTitle && subStep.hasError
          );
        });

        if (firstErrorAction) {
          const newActions = actions.filter((a) => a !== firstErrorAction);
          newActions.push(firstErrorAction);
          setValue('actionInfo.actionTodo', newActions, {
            shouldValidate: true,
          });

          setExpandedIds(new Set([firstErrorAction]));
          setTimeout(() => {
            const element = document.getElementById(firstErrorAction!);
            element?.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }, 100);
        }
      }
    }
  };

  const {
    showDialog: showUnsavedChangesDialog,
    handleConfirm: handleUnsavedConfirm,
    handleCancel: handleUnsavedCancel,
    isCancelAction,
    bypass,
  } = useUnsavedChanges(isDirty);

  const handleSaveAndClose = () => {
    const requiredRole =
      pageMode === 'edit'
        ? RulesEngineRoles.EditRules.value
        : RulesEngineRoles.AddRules.value;
    if (isAbleToSeeUIElement(changeLogRoles, requiredRole)) {
      setDialogSubmitAction('draft');
      setOpenChangeLogDialog(true);
    } else {
      const currentData = getValues();
      saveDraft({ ...currentData, showSnackbar: true });
    }
  };

  const handleCreatePolicy = () => {
    const currentData = getValues();
    saveDraft(
      { ...currentData, showSnackbar: false },
      {
        onSuccess: (savedRule) => {
          setSnackbar({
            open: true,
            severity: 'success',
            message: 'Rule saved as draft. You can resume editing anytime.',
          });
          bypass();
          navigate(
            `/${pagesConfig.policies.link}/${pagesConfig.createPolicy.link}`,
            { state: { resumeRuleId: savedRule.id } }
          );
        },
      }
    );
  };

  const handleCreateEmailTemplate = () => {
    const currentData = getValues();
    saveDraft(
      { ...currentData, showSnackbar: false },
      {
        onSuccess: (savedRule) => {
          setSnackbar({
            open: true,
            severity: 'success',
            message: 'Rule saved as draft. You can resume editing anytime.',
          });
          bypass();
          navigate(
            `/${pagesConfig.settings.link}/${pagesConfig.welcomeTemplates.link}/create`,
            {
              state: { resumeRuleId: savedRule.id },
            }
          );
        },
      }
    );
  };

  const formStepperProps = {
    activeStep,
    setActiveStep,
    steps,
    errors,
    trigger,
    stepCompleted,
    setStepCompleted,
  };

  const isLoadingSharedDrives =
    sharedDriveIdsToFetch.length > 0 && !sharedDrives;

  const isRuleDataLoading =
    isLoadingAllRules || pendingUserDriveFolders || isLoadingSharedDrives;

  const isLoading =
    isPolicyLoading ||
    isPolicyFetching ||
    (pageMode !== 'create' && isRuleDataLoading);

  function getFirstIdWithError(): string | null {
    if (errors.policyInfo) return 'runPolicy';
    if (errors.emailInfo) return 'sendEmail';
    if (errors.chatInfo) return 'sendChatMessage';
    if (errors.groupInfo) return 'addToGroup';
    if (errors.removeGroupInfo) return 'removeFromGroup';
    if (errors.chatInfo) return 'sendChatMessage';
    if (errors.actionInfo?.occurrenceLimit) return 'limitOccurrences';
    return null;
  }

  const firstIdWithError = getFirstIdWithError();

  useEffect(() => {
    setStepCompleted(steps.map(() => false));
    clearErrors();
  }, [triggerGroup, triggerEvent, steps, clearErrors]);

  // Handle invalid clone or edit ID after loading is complete
  if (!isLoadingAllRules && pageMode !== 'create' && !sourceRuleForForm) {
    return <NotFoundPage />;
  }

  const handleOpenDrawer = (rule: RuleModel) => {
    setSearchParams(
      (prev) => {
        prev.set('selectedRule', rule.id.toString());
        return prev;
      },
      { state: location.state }
    );
  };

  const handleCloseDrawer = () => {
    if (pageMode === 'edit') {
      navigate(
        `/${pagesConfig.rulesEngine.link}/edit/${selectedRuleIdFromPath}`
      );
    } else {
      setSearchParams(
        (prev) => {
          prev.delete('selectedRule');
          return prev;
        },
        { state: location.state }
      );
    }
  };

  const updateSelectedRuleStatus = (status: 'Enabled' | 'Disabled') => {
    if (selectedRule) {
      // Also update the query cache for the main grid
      queryClient.setQueryData(
        [RulesKeys.RulesList],
        (oldData: RuleModel[] | undefined) => {
          return oldData
            ? oldData.map((r) =>
                r.id === selectedRule.id
                  ? { ...r, basicInfo: { ...r.basicInfo, status } }
                  : r
              )
            : undefined;
        }
      );
    }
  };
  const handleEnableRule = () => {
    setOpenEnableRuleDialog(true);
  };

  const handleDisableRule = () => {
    setOpenDisableRuleDialog(true);
  };

  const handleDeleteRule = () => {
    setOpenDeleteRuleDialog(true);
  };

  const handleEditRule = (rule: RuleModel) => {
    if (rule.basicInfo.status === 'Draft') {
      navigate(`/${pagesConfig.rulesEngine.link}/edit/${rule.id}`);
    } else {
      setOpenEditRuleDialog(true);
    }
  };

  const handleCloneRule = (ruleToClone: RuleModel) => {
    if (!ruleToClone) return;
    navigate(
      `/${pagesConfig.rulesEngine.link}/${pagesConfig.cloneRule.link}/${ruleToClone.id}`
    );
  };

  const handleDeleteSuccess = () => {
    if (!selectedRule) return;
    queryClient.setQueryData(
      [RulesKeys.RulesList],
      (oldData: RuleModel[] | undefined) =>
        oldData ? oldData.filter((r) => r.id !== selectedRule.id) : oldData
    );
    handleCloseDrawer();
    setOpenDeleteRuleDialog(false);
  };

  return (
    <SimpleBreadcrumbsPage
      isSubPage
      breadcrumbs={[
        { label: ModuleList.modules.automation.name },
        {
          label: pagesConfig.rulesEngine.name,
          path: pagesConfig.rulesEngine.link,
        },
      ]}
    >
      <Stack
        direction="row"
        sx={{ height: '100%', gap: 1, overflow: 'hidden' }}
      >
        <Box sx={{ flex: 1, minWidth: 0, height: '100%' }}>
          <SimpleFormPage
            title={title}
            stepper={
              <SharedFormStepper<RulesEngineFormValues>
                {...formStepperProps}
                subStep={actionSubSteps}
              />
            }
            {...formStepperProps}
            content={
              <Box
                sx={{
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                {isLoading && <RulesEngineFormSkeleton />}
                {!isLoading &&
                  steps[activeStep].label === 'Basic Information' && (
                    <RulesEngineBasicInformationStep
                      control={control}
                      errors={errors}
                      watch={watch}
                      setValue={setValue}
                      trigger={trigger}
                      sourceType={sourceType}
                      setSourceType={setSourceType}
                      getValues={getValues}
                      domains={domains || []}
                      isCalendarResourceEvent={
                        triggerGroup === 'calendar' &&
                        triggerEvent != 'change_calendar_acls'
                      }
                      setSelectedRule={handleOpenDrawer}
                      allRules={allRulesFromApi ?? []}
                    />
                  )}
                {!isLoading && steps[activeStep].label === 'Admin Filters' && (
                  <RulesEngineAdminFiltersStep
                    control={control}
                    watch={watch}
                    setValue={setValue}
                    clearErrors={clearErrors}
                  />
                )}
                {!isLoading && steps[activeStep].label === 'Group Filters' && (
                  <RulesEngineGroupFiltersStep
                    control={control}
                    watch={watch}
                    setValue={setValue}
                    clearErrors={clearErrors}
                  />
                )}
                {!isLoading &&
                  steps[activeStep].label === 'Personal Calendar Filters' && (
                    <RulesEnginePersonalCalendarFiltersStep
                      control={control}
                      watch={watch}
                      setValue={setValue}
                      errors={errors}
                      clearErrors={clearErrors}
                    />
                  )}
                {!isLoading && steps[activeStep].label === 'Drive Filters' && (
                  <DriveFiltersStep
                    control={control}
                    errors={errors}
                    watch={watch}
                    setValue={setValue}
                    trigger={trigger}
                    clearErrors={clearErrors}
                  />
                )}
                {!isLoading &&
                  steps[activeStep].label === 'Trigger Sources' && (
                    <CalendarResourcesStep
                      control={control}
                      errors={errors}
                      watch={watch}
                      setValue={setValue}
                      trigger={trigger}
                      clearErrors={clearErrors}
                    />
                  )}
                {!isLoading && steps[activeStep].label === 'Actions' && (
                  <ActionsStep
                    control={control}
                    errors={errors}
                    watch={watch}
                    allPolicies={allPolicies || []}
                    expandedIds={expandedIds}
                    setExpandedIds={setExpandedIds}
                    setValue={setValue}
                    trigger={trigger}
                    setActionSubSteps={setActionSubSteps}
                    clearErrors={clearErrors}
                    toolsRoles={toolsRoles}
                    onCreatePolicy={handleCreatePolicy}
                    onCreateEmailTemplate={handleCreateEmailTemplate}
                    isSavingDraft={isSavingDraft}
                    autoSelectEmailTemplateId={autoSelectEmailTemplateId}
                  />
                )}
                {!isLoading && steps[activeStep].label === 'Review' && (
                  <RulesEngineReviewStep
                    selectedRule={{
                      ...getValues(),
                      triggerInfo: {
                        ...getValues().triggerInfo,
                        triggerCalendarSources:
                          getValues().triggerInfo.triggerCalendarSources ?? [],
                      },
                    }}
                    allPolicies={allPolicies}
                  />
                )}
              </Box>
            }
            handleDiscard={handleCancel}
            handleNext={handleNext}
            handleSubmit={handleSubmit(onSubmit)}
            submitButtonText="Save and Enable"
            isLoadingSubmit={isValidating || isSubmitting || isPending}
            isDisableSubmit={isLoading}
            firstIdWithError={firstIdWithError ?? undefined}
            setExpandedIds={setExpandedIds}
            lastSavedDate={
              lastSavedDate ?? sourceRuleForForm?.basicInfo.lastModifiedDate
            }
            hasBeenSaved={hasBeenSaved}
            handleSaveAndClose={handleSaveAndClose}
            isSaving={isSavingDraft}
            isFormDirty={isDirty || fromTemplates}
            status={selectedRule?.basicInfo.status}
          />
        </Box>

        <Box
          sx={{
            flexShrink: 0,
            height: '100%',
            display:
              activeStep === steps.length - 1
                ? 'none'
                : { xs: 'none', lg: 'block' },
          }}
        >
          <RuleSummaryPanel
            watch={watch}
            getModel={() => prepareRuleData(getValues())}
            disablePreview={['Actions'].includes(steps[activeStep]?.label)}
          />
        </Box>
      </Stack>

      <UnsavedChangesDialog
        isCancelAction={isCancelAction}
        pageMode={pageMode}
        entityName="Rule"
        open={showUnsavedChangesDialog}
        handleConfirm={handleUnsavedConfirm}
        handleClose={handleUnsavedCancel}
      />

      <ChangeLogDialog
        open={openChangeLogDialog}
        handleClose={() => {
          setOpenChangeLogDialog(false);
          setDialogSubmitAction(null);
        }}
        changeLogMessage={changeLogMessage}
        setChangeLogMessage={setChangeLogMessage}
        mutate={() => {
          if (dialogSubmitAction === 'publish') {
            mutate(getValues());
          } else if (dialogSubmitAction === 'draft') {
            saveDraft({ ...getValues(), showSnackbar: true });
          }
        }}
        mutateLoading={isPending || isSavingDraft}
      />

      <Drawer
        anchor="right"
        open={openRulesDetailsDrawer}
        onClose={handleCloseDrawer}
        sx={{
          '& .MuiDrawer-paper': {
            width: { xs: '100vw', md: '45%' },
          },
        }}
      >
        {isLoadingAllRules && (
          <Stack
            sx={{ width: '100%', height: '100%' }}
            justifyContent="center"
            alignItems="center"
          >
            <CircularProgress />
          </Stack>
        )}

        {!isLoadingAllRules && selectedRule && allPolicies && (
          <RulesEngineDrawerDetailsOld
            selectedRule={selectedRule}
            allPolicies={allPolicies}
            rules={allRulesFromApi || []}
            onClose={handleCloseDrawer}
            onEnable={handleEnableRule}
            onDisable={handleDisableRule}
            onDelete={handleDeleteRule}
            onEdit={handleEditRule}
            onClone={handleCloneRule}
          />
        )}
      </Drawer>

      {openDeleteRuleDialog && selectedRule && (
        <DeleteRuleDialog
          open={openDeleteRuleDialog}
          handleClose={() => setOpenDeleteRuleDialog(false)}
          changeLogRoles={changeLogRoles}
          selectedRules={[selectedRule]}
          setSelectedRows={() => {}}
          onSuccess={handleDeleteSuccess}
        />
      )}
      {openDisableRuleDialog && selectedRule && (
        <DisableRuleDialog
          open={openDisableRuleDialog}
          handleClose={() => setOpenDisableRuleDialog(false)}
          changeLogRoles={changeLogRoles}
          selectedRules={[selectedRule]}
          setSelectedRows={() => {}}
          onSuccess={() => updateSelectedRuleStatus('Disabled')}
        />
      )}
      {openEnableRuleDialog && selectedRule && (
        <EnableRuleDialog
          open={openEnableRuleDialog}
          handleClose={() => setOpenEnableRuleDialog(false)}
          changeLogRoles={changeLogRoles}
          selectedRules={[selectedRule]}
          setSelectedRows={() => {}}
          onSuccess={() => updateSelectedRuleStatus('Enabled')}
        />
      )}
      {openEditRuleDialog && selectedRule && (
        <EditRuleDialog
          open={openEditRuleDialog}
          handleClose={() => setOpenEditRuleDialog(false)}
          rule={selectedRule}
          onEditExisting={() => {
            setOpenEditRuleDialog(false);
            navigate(
              `/${pagesConfig.rulesEngine.link}/edit/${selectedRule.id}`
            );
          }}
          onCreateNew={() => {
            const view = searchParams.get('view') || '';
            navigate(
              `/${pagesConfig.rulesEngine.link}/${pagesConfig.createRule.link}${
                view ? `?view=${view}` : ''
              }`
            );
            handleCloseDrawer();
            setOpenEditRuleDialog(false);
            reset();
          }}
        />
      )}
    </SimpleBreadcrumbsPage>
  );
}
